import { vehicleString } from '../../../../utilities';

const responseTypes = {
  single: ['SELECT_SINGLE', 'DROPDOWN_SINGLE', 'USER', 'TEST', 'ASSET'],
  multi: ['SELECT_MULTI', 'DROPDOWN_MULTI', 'SELECT_CHECKLIST', 'USER_MULTI'],
  number: ['NUMBER'],
  other: ['TABLE', 'MATRIX'],
  file: ['FILE_SINGLE', 'FILE_MULTI'],
  image: ['IMAGE', 'IMAGE_MULTI'],
  signature: ['SIGNATURE_SINGLE', 'SIGNATURE_MULTI'],
};

export function computeQuestionStats(question, items) {
  const type = String(
    question?.type || question?.inputType || '',
  ).toUpperCase();

  const getValue = (it) => (it?.value ?? '').toString().trim();
  const answeredItems = (items || []).filter((it) => getValue(it).length > 0);
  const total = answeredItems.length;

  const split = (s) =>
    String(s ?? '')
      .split('|')
      .map((t) => t.trim())
      .filter(Boolean);

  const templateOptions = (() => {
    const opts = split(question?.additionalData || question?.additional);
    if (opts.length) return opts;
    if (type === 'TEST') return ['PASS', 'FAIL', 'NOT_APPLICABLE'];
    return [];
  })();

  if (total === 0) return { kind: 'empty', total };

  const { single, multi, number, other, file, image, signature } =
    responseTypes;

  const pushOptionRows = (counts, denom, templateOptions) => {
    const rows = [];
    if (templateOptions.length) {
      for (const opt of templateOptions) {
        const count = counts.get(opt) || 0;
        rows.push({
          key: opt,
          label: opt,
          count,
          value: count,
          pct: Math.round((count / Math.max(1, denom)) * 100),
          options: templateOptions,
        });
      }
      for (const [val, count] of counts) {
        if (!templateOptions.includes(val)) {
          rows.push({
            key: val,
            label: val,
            count,
            value: count,
            pct: Math.round((count / Math.max(1, denom)) * 100),
          });
        }
      }
    } else {
      for (const [val, count] of counts) {
        rows.push({
          key: val,
          label: val,
          count,
          value: count,
          pct: Math.round((count / Math.max(1, denom)) * 100),
        });
      }
      rows.sort(
        (a, b) =>
          b.count - a.count || String(a.label).localeCompare(String(b.label)),
      );
    }
    return rows;
  };

  if (single.includes(type)) {
    const counts = new Map();
    for (const it of answeredItems) {
      if (type === 'USER') {
        const hasId = !!it?.user?.id;
        if (!hasId) continue;
        const label =
          [it.user?.firstName, it.user?.lastName].filter(Boolean).join(' ') ||
          String(it.user?.id);
        if (label) counts.set(label, (counts.get(label) || 0) + 1);
      } else if (type === 'ASSET') {
        const hasId = !!it?.vehicle?.id;
        if (!hasId) continue;
        const label =
          (typeof vehicleString === 'function' && vehicleString(it.vehicle)) ||
          `Asset ${it.vehicle?.id}`;
        if (label) counts.set(label, (counts.get(label) || 0) + 1);
      } else if (type === 'TEST') {
        let v = getValue(it);
        const [firstOpt, secondOpt, thirdOpt] = templateOptions;
        switch (v) {
          case 'PASS':
            v = firstOpt;
            break;
          case 'FAIL':
            v = secondOpt;
            break;
          case 'NOT_APPLICABLE':
            v = thirdOpt;
            break;
          default:
            break;
        }
        if (v) counts.set(v, (counts.get(v) || 0) + 1);
      } else {
        const v = getValue(it);
        if (v) counts.set(v, (counts.get(v) || 0) + 1);
      }
    }
    return {
      kind: 'single',
      total,
      rows: pushOptionRows(counts, total, templateOptions),
    };
  }

  if (multi.includes(type)) {
    if (type === 'USER_MULTI') {
      const counts = new Map();
      for (const it of answeredItems) {
        const users = Array.isArray(it?.users) ? it.users : [];
        if (!users.length) {
          continue;
        }
        const seen = new Set();
        for (const u of users) {
          const id = String(u?.id ?? '').trim();
          if (!id || seen.has(id)) continue;
          seen.add(id);
          const label =
            [u.firstName, u.lastName].filter(Boolean).join(' ') || id;
          const cur = counts.get(id);
          if (cur) cur.count += 1;
          else counts.set(id, { count: 1, label });
        }
      }
      const rows = Array.from(counts, ([id, { count, label }]) => ({
        key: id,
        label: label || id,
        count,
        value: count,
        pct: Math.round((count / Math.max(1, total)) * 100),
      })).sort(
        (a, b) =>
          b.count - a.count || String(a.label).localeCompare(String(b.label)),
      );
      return { kind: 'multi', total, rows };
    } else {
      const counts = new Map();
      for (const it of answeredItems) {
        const tokens = split(it?.value);
        if (!tokens.length) continue;
        for (const t of tokens) {
          counts.set(t, (counts.get(t) || 0) + 1);
        }
      }
      return {
        kind: 'multi',
        total,
        rows: pushOptionRows(counts, total, templateOptions),
      };
    }
  }

  if (number.includes(type)) {
    const examples = [];
    for (const it of answeredItems || []) {
      const raw = it?.value;
      const n = typeof raw === 'number' ? raw : parseFloat(raw);
      if (!Number.isFinite(n)) continue;
      const ts = it?.dateCreated || null;

      examples.push({
        value: n,
        dateCreated: ts,
      });
    }

    if (!examples.length) {
      return { kind: 'numeric', total, count: 0, examples: [] };
    }

    const hasDates = examples.some((e) => e.dateCreated);
    const sorted = hasDates
      ? examples
          .map((e) => ({
            ...e,
            _t: e.dateCreated
              ? new Date(e.dateCreated).getTime()
              : Number.POSITIVE_INFINITY,
          }))
          .sort((a, b) => a._t - b._t)
          .map(({ _t, ...rest }) => rest)
      : examples;

    const nums = sorted.map((e) => e.value);
    const sum = nums.reduce((a, b) => a + b, 0);

    return {
      kind: 'numeric',
      total,
      count: nums.length,
      avg: sum / nums.length,
      min: Math.min(...nums),
      max: Math.max(...nums),
      examples: sorted,
      sum,
    };
  }

  if (image.includes(type)) {
    const examples = [];
    if (type === 'IMAGE_MULTI') {
      for (const it of answeredItems) {
        const urls = split(it?.value);
        for (const url of urls) {
          if (url) examples.push(url);
        }
      }
    } else {
      for (const it of answeredItems) {
        const url = getValue(it);
        if (url) examples.push(url);
      }
    }
    return { kind: 'image', total, examples };
  }

  if (file.includes(type)) {
    const examples = [];
    if (type === 'FILE_MULTI') {
      for (const it of answeredItems) {
        const urls = split(it?.value);
        const names = split(it?.additional);
        const n = Math.max(urls.length, names.length);
        for (let i = 0; i < n; i++) {
          const url = urls[i]?.trim();
          const name = (names[i]?.trim() || url || 'Attachment').trim();
          if (!url && !name) continue;
          examples.push({ name, url });
        }
      }
    } else {
      for (const it of answeredItems) {
        const url = getValue(it);
        const name =
          (it?.additional ?? '').toString().trim() || url || 'Attachment';
        if (!url && !name) continue;
        examples.push({ name, url });
      }
    }
    return { kind: 'file', total, examples };
  }

  if (signature.includes(type)) {
    const counts = new Map();
    for (const it of answeredItems) {
      const names = split(it?.additional);
      if (type === 'SIGNATURE_MULTI') {
        for (const raw of names) {
          const key = String(raw).trim().toLowerCase();
          if (!key) continue;
          counts.set(key, (counts.get(key) || 0) + 1);
        }
      } else {
        const raw = (it?.additional ?? '').toString();
        const key = raw.trim().toLowerCase();
        if (key) counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
    const rows = Array.from(counts, ([label, count]) => ({
      key: label,
      label,
      count,
      value: count,
      pct: Math.round((count / Math.max(1, total)) * 100),
    })).sort(
      (a, b) =>
        b.count - a.count || String(a.label).localeCompare(String(b.label)),
    );
    return { kind: 'signature', total, rows };
  }

  if (other.includes(type) && type === 'MATRIX') {
    const toNum = (x) => {
      const n = typeof x === 'number' ? x : parseFloat(x);
      return Number.isFinite(n) ? n : null;
    };
    const impactCounts = new Map();
    const probCounts = new Map();
    const riskCounts = new Map();
    const riskValues = [];

    for (const it of answeredItems) {
      const impact = toNum(it?.impact) ?? toNum(it?.meta?.impact) ?? null;
      const probability =
        toNum(it?.probability) ?? toNum(it?.meta?.probability) ?? null;

      if (impact != null)
        impactCounts.set(
          String(impact),
          (impactCounts.get(String(impact)) || 0) + 1,
        );
      if (probability != null)
        probCounts.set(
          String(probability),
          (probCounts.get(String(probability)) || 0) + 1,
        );

      if (impact != null && probability != null) {
        const risk = impact * probability;
        const riskKey = Number.isInteger(risk)
          ? String(risk)
          : String(Math.round(risk * 100) / 100);
        riskCounts.set(riskKey, (riskCounts.get(riskKey) || 0) + 1);
        riskValues.push(risk);
      }
    }

    const toRows = (map, prefix = '') => {
      const rows = Array.from(map, ([label, count]) => ({
        key: label,
        label: prefix ? `${prefix} ${label}` : label,
        count,
        value: count,
        pct: Math.round((count / Math.max(1, total)) * 100),
      }));
      rows.sort((a, b) => {
        const an = Number(a.key);
        const bn = Number(b.key);
        if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
        return String(a.label).localeCompare(String(b.label));
      });
      return rows;
    };

    const impactRows = toRows(impactCounts, 'Level');
    const probabilityRows = toRows(probCounts, 'Level');
    const riskRows = toRows(riskCounts);

    const minRisk = riskValues.length ? Math.min(...riskValues) : null;
    const maxRisk = riskValues.length ? Math.max(...riskValues) : null;
    const avgRisk = riskValues.length
      ? riskValues.reduce((a, b) => a + b, 0) / riskValues.length
      : null;

    return {
      kind: 'matrix',
      total,
      impactRows,
      probabilityRows,
      riskRows,
      minRisk,
      maxRisk,
      avgRisk,
      riskCount: riskValues.length,
    };
  }

  if (other.includes(type) && type === 'TABLE') {
    const splitHeaders = (s) =>
      String(s ?? '')
        .split('|')
        .map((t) => t.trim());

    const headers = question?.additionalData?.trim()
      ? splitHeaders(question.additionalData)
      : splitHeaders(question?.additional);

    const colCount = headers.length || 0;

    const rows = [];
    const groupStarts = [];

    for (const it of answeredItems) {
      const raw = getValue(it);
      if (!raw) continue;

      const lines = raw
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

      let firstInThisEntry = true;

      for (const line of lines) {
        const cells = line.split('|').map((c) => c.trim());
        let aligned = cells;

        if (colCount > 0) {
          if (cells.length < colCount) {
            aligned = [...cells, ...Array(colCount - cells.length).fill('')];
          } else if (cells.length > colCount) {
            const head = cells.slice(0, colCount - 1);
            const tailJoined = cells.slice(colCount - 1).join(' | ');
            aligned = [...head, tailJoined];
          }
        }

        // push row
        rows.push(aligned);

        // mark start of a new entry (first row from this answeredItem)
        if (firstInThisEntry) {
          groupStarts.push(rows.length - 1);
          firstInThisEntry = false;
        }
      }
    }

    return { kind: 'table', total, headers, rows, groupStarts };
  }

  const examples = answeredItems.map((it) => getValue(it)).filter(Boolean);

  return { kind: 'text', total, examples };
}

export const hasFollowUpQuestion = (question) => {
  const conditions = question?.conditionalAction;
  if (!Array.isArray(conditions)) return false;
  return conditions.some((ca) =>
    ca.actions?.some(
      (a) =>
        a.action === 'FOLLOW_UP_QUESTION' &&
        a.actionable?.inputType !== 'MESSAGE',
    ),
  );
};

export const getAllQuestionsFlatMap = (questions) => {
  if (questions?.length < 1) {
    return [];
  }
  const allQuestions = [];

  for (const question of questions) {
    if (question?.inputType === 'MESSAGE') {
      continue;
    } else {
      allQuestions.push(question);
      if (hasFollowUpQuestion(question)) {
        question.conditionalAction
          .flatMap((ca) => ca.actions ?? [])
          .forEach((a) => {
            if (
              a.action === 'FOLLOW_UP_QUESTION' &&
              a.actionable?.inputType !== 'MESSAGE'
            ) {
              allQuestions.push(a.actionable);
            }
          });
      }
    }
  }
  return allQuestions;
};

export const getQuestionsFromCategory = (category) => {
  return [
    ...getAllQuestionsFlatMap(category?.questions ?? []),
    ...(category?.categories ?? []).flatMap(getQuestionsFromCategory),
  ];
};

export const getAllQuestionsFromTemplate = (template) => {
  return (template?.categories ?? []).flatMap(getQuestionsFromCategory);
};

export const getRelatedQuestionIds = (question, templates) => {
  const targetTemplate = templates.find(
    (t) => t?.id === question?.inspectionTemplateId,
  );
  const hasRevs = targetTemplate?.parentId !== null;

  if (hasRevs) {
    const revs = templates.filter(
      (t) => t.parentId === targetTemplate?.parentId,
    );
    const original = templates.filter((t) => t.id === targetTemplate?.parentId);

    const allQuestions = [...(revs || []), ...(original || [])].flatMap(
      getAllQuestionsFromTemplate,
    );

    const relatedQuestions = allQuestions.filter(
      (q) => q?.questionLineageId === question?.questionLineageId,
    );

    return relatedQuestions;
  } else {
    return question;
  }
};
